import { __rest } from "tslib";
import React, { useContext, useState, useEffect } from 'react';
import Measure from 'react-measure';
import classnames from 'classnames';
import { usePopper } from 'react-popper';
import uniqueId from 'lodash/uniqueId';
import { calculateWidth, fixedDateTimeRange, getDurationText, getScacles, isRangeInteract } from './util';
import { GroupContext } from './context';
import styles from './timelinepicker.module.scss';
import SiriusMessage from '@web-common/components/UI/Message/SiriusMessage';
import { getIn18Text } from 'api';
const TimeLinePicker = props => {
    const provider = useContext(GroupContext);
    const mergedProps = Object.assign(Object.assign({}, provider.props), props);
    const { occupied: propsOccupied, forbidden: propsForbidden, onChange, date, value, defaultValue, renderOccupiedInner, renderEnableInner, renderForbiddenInner, startHour = 7, endHour = 23, pieceOfHour = 4, className, onOk } = mergedProps, rest = __rest(mergedProps, ["occupied", "forbidden", "onChange", "date", "value", "defaultValue", "renderOccupiedInner", "renderEnableInner", "renderForbiddenInner", "startHour", "endHour", "pieceOfHour", "className", "onOk"]);
    const [width, setWidth] = useState(0);
    const [range, setRange] = useState(defaultValue);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [id] = useState(uniqueId('siri_timeline_pick_'));
    const { styles: popStyles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'top',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 8],
                },
            },
        ],
    });
    const occupied = fixedDateTimeRange(propsOccupied);
    const fixedPropsForbidden = fixedDateTimeRange(propsForbidden);
    const forbidden = fixedPropsForbidden;
    const handleScaleUnitClick = (eventValue, event) => {
        provider.onIdChange && provider.onIdChange(id);
        if (range !== undefined) {
            const [prevStart, prevEnd] = range;
            const [eventStart, eventEnd] = eventValue;
            const contiueous = prevEnd.diff(prevStart, 'hours', true) > 1 / pieceOfHour;
            if (contiueous) {
                const isInside = eventStart.isBetween(prevStart, prevEnd, undefined, '[]') && eventEnd.isBetween(prevStart, prevEnd, undefined, '[]');
                if (isInside) {
                    setRange([prevStart, eventEnd]);
                }
                else {
                    setRange(eventValue);
                }
            }
            else {
                if (eventStart.isSame(prevStart) && eventEnd.isSame(prevEnd)) {
                    setRange(undefined);
                    return;
                }
                const nextRange = eventStart.isBefore(prevStart) ? [eventStart, prevEnd] : [prevStart, eventEnd];
                const interact = isRangeInteract(nextRange, occupied) || isRangeInteract(nextRange, forbidden);
                if (interact) {
                    SiriusMessage.info({ content: getIn18Text('QINGXUANZELIANXU') });
                    return;
                }
                setRange(nextRange);
            }
        }
        else {
            setRange(eventValue);
        }
        setReferenceElement(event.target);
    };
    useEffect(() => {
        if (onChange && range) {
            onChange(range);
        }
    }, [range, onChange]);
    useEffect(() => {
        if (provider.uniqueId !== id) {
            setRange(undefined);
        }
    }, [provider.uniqueId, id]);
    useEffect(() => {
        const fadeOut = (e) => {
            let node = e.target;
            while (node && node !== popperElement) {
                node = node.parentNode;
            }
            if (!node) {
                setRange(undefined);
            }
        };
        document.addEventListener('click', fadeOut);
        return () => {
            document.removeEventListener('click', fadeOut);
        };
    }, [popperElement, range]);
    const scales = getScacles({
        startHour,
        endHour,
        pieceOfHour,
        date,
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Measure, { bounds: true, onResize: cr => {
                var _a;
                setWidth(((_a = cr.bounds) === null || _a === void 0 ? void 0 : _a.width) || 0);
            } }, ({ measureRef }) => (React.createElement("div", Object.assign({ ref: measureRef, className: classnames(styles.scale, className) }, rest),
            scales.map((s, index) => {
                const selectValue = value || range;
                return (React.createElement("div", { key: index, style: { width: width / scales.length }, className: classnames(styles.scaleUnit, {
                        [styles.scaleUnitSelect]: selectValue !== undefined && s[0].isBetween(selectValue[0], selectValue[1], undefined, '[)'),
                        [styles.scaleUnitFirst]: index === 0,
                        [styles.scaleUnitLast]: index === scales.length - 1,
                    }), onClick: e => handleScaleUnitClick(s, e) },
                    renderEnableInner !== undefined && renderEnableInner(s, index),
                    width > 0 && (React.createElement(React.Fragment, null,
                        index % pieceOfHour === 0 && React.createElement("span", { className: styles.scaleUnitTag }, s[0].format('HH')),
                        index === scales.length - 1 && (React.createElement("span", { className: classnames(styles.scaleUnitTag, styles.scaleUnitTagLast) }, s[0].clone().add(1, 'hours').format('HH')))))));
            }), occupied === null || occupied === void 0 ? void 0 :
            occupied.map((s, index) => {
                const { startOverflow, endOverflow, elementWidth, elementLeft } = calculateWidth(s, scales, width, startHour, pieceOfHour);
                return (React.createElement("div", { key: index, className: classnames(styles.scaleOccupied, {
                        [styles.scaleOccupiedMale]: startOverflow || elementLeft === 0,
                        [styles.scaleOccupiedFemale]: endOverflow || elementLeft === width - elementWidth,
                    }), style: {
                        width: elementWidth,
                        left: elementLeft,
                    } }, renderOccupiedInner !== undefined && renderOccupiedInner(s, index)));
            }), forbidden === null || forbidden === void 0 ? void 0 :
            forbidden.map((s, index) => {
                const { startOverflow, endOverflow, elementWidth, elementLeft } = calculateWidth(s, scales, width, startHour, pieceOfHour);
                return (React.createElement("div", { key: index, className: classnames(styles.scaleForbidden, {
                        [styles.scaleForbiddenMale]: startOverflow,
                        [styles.scaleForbiddenFemale]: endOverflow,
                    }), style: {
                        width: elementWidth,
                        left: elementLeft,
                    } }, renderForbiddenInner !== undefined && renderForbiddenInner(s, index)));
            })))),
        range !== undefined && (React.createElement("div", Object.assign({ ref: setPopperElement, style: popStyles.popper, className: styles.popper }, attributes.popper),
            React.createElement("p", { className: styles.title }, getIn18Text('NINXUANZEDESHI')),
            React.createElement("p", { className: styles.content }, getDurationText(range)),
            React.createElement("div", { className: styles.footer },
                React.createElement("button", { className: styles.default, onClick: () => {
                        setRange(undefined);
                    } }, getIn18Text('QUXIAO')),
                React.createElement("button", { className: styles.primary, onClick: () => {
                        onOk && onOk(range);
                    } }, getIn18Text('QUEDING')))))));
};
export default TimeLinePicker;
